import * as React from "react";
import { action, observable, IObservableArray, makeAutoObservable, computed, reaction } from "mobx";
import { guid } from "utils";
import { statusFetching } from "services/authClient/constants/types";

export type ActionModalBody = (data: {
  actionData?: any;
}) => React.ComponentElement<{ onClose?: () => any; actionData?: any }, any>;

export interface ActionModalOptions {
  width?: string;
  position?: "flex-start" | "center";
  title?: string;
  onApply?: (data?: any) => Promise<any>;
  onClose?: (data?: any) => any;
  applyText?: string | React.ReactElement | React.FC<{ onClick: () => void; disabled: boolean }>;
  closeText?: string | React.FC;
  noValidation?: boolean;
  initData?: any;
  showOther?: boolean;
  emulateClass?: boolean;
  hideHeader?: boolean;
  centered?: boolean;
  isOverlayClose?: boolean;
  onCloseHeader?: () => void;
}

export interface ActionModalConfig {
  id: string;
  render: ActionModalBody | any;
  options: ActionModalOptions;
  error?: string;
  actionStatus?: statusFetching;
}

export class ActionModalsStore {
  @observable public pool: Array<ActionModalConfig> = observable.array([], {
    deep: true,
  });

  @computed get isAnyModalOpen(): boolean {
    return this.pool.length > 0;
  }

  @computed get modalIds(): string[] {
    return this.pool.map((modal) => modal.id);
  }

  constructor() {
    makeAutoObservable(this);

    // Реакция на изменения в pool
    reaction(
      () => this.pool.length,
      (length) => {
        document.body.style.overflow = length > 0 ? "hidden" : "";
      }
    );
  }

  @action.bound
  public open = (render: ActionModalBody | any, options: ActionModalOptions): Promise<any> => {
    const id = guid();

    const modalConfig: ActionModalConfig = {
      render,
      options,
      id,
      error: "",
      actionStatus: "init",
    };

    const deferPromise = new Promise((resolve, reject) => {
      const onApply = options.onApply;

      if (onApply) {
        options.onApply = (data: any) =>
          onApply(data)
            .then(resolve)
            .then(() => this.close(id))
            .catch((err) => {
              const currentModal = this.pool.find((m) => m.id === id);

              if (currentModal) {
                currentModal.error = err?.detail || err?.title || err?.message;
              }

              reject(err);

              return Promise.reject(err);
            });
      }
    });

    this.pool.push(observable(modalConfig));
    document.body.style.overflow = "hidden";

    return deferPromise;
  };

  @action.bound
  public close = (id: string) => {
    const modalConfig = this.pool.find((modal) => modal.id === id);

    if (modalConfig) {
      if (modalConfig.options.onClose) {
        modalConfig.options.onClose();
      }

      this.pool = this.pool.filter((modal) => modal.id !== id);

      if (this.pool.length === 0) {
        document.body.style.overflow = "";
      }
    }
  };

  @action.bound
  public closeLastModal = () => {
    this.close(this.pool[this.pool.length - 1].id);
  };

  @action.bound
  rejectError = (id: string, err: any, reject?: any) => {
    let message = err;
    if (err.message) {
      message = err.message;
    }

    //@ts-ignore
    this.pool.find((m) => m.id === id).error = message;

    if (reject) reject();
    return Promise.reject(err);
  };

  @action.bound
  public closeAll = () => {
    (this.pool as IObservableArray<ActionModalConfig>).clear();
    document.body.style.overflow = "";
  };
}
